/**
 * Timezone utility functions for consistent timezone handling across the application
 */
import { zonedTimeToUtc } from 'date-fns-tz';

/**
 * Normalizes timezone string by trimming whitespace
 * @param timezone - Timezone string to normalize
 * @returns Trimmed timezone string or null if empty/invalid
 */
export function normalizeTimezone(
  timezone: string | null | undefined
): string | null {
  if (!timezone || typeof timezone !== 'string') {
    return null;
  }
  return timezone.trim() || null;
}

/**
 * Gets tenant timezone from app context with fallback to browser timezone
 * @param tenant - Tenant object from useAppContext
 * @returns IANA timezone identifier
 */
export function getTenantTimezone(
  tenant: { time_zone?: string } | null | undefined
): string {
  if (tenant?.time_zone) {
    // Normalize and validate the timezone before returning
    const normalized = normalizeTimezone(tenant.time_zone);
    if (normalized && isValidTimezone(normalized)) {
      return normalized;
    }
  }
  // Fallback to browser timezone if tenant timezone is not available or invalid
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Validates that a timezone string is a valid IANA timezone identifier
 * @param timezone - Timezone string to validate
 * @returns true if valid, false otherwise
 */
export function isValidTimezone(timezone: string | null | undefined): boolean {
  if (!timezone || typeof timezone !== 'string') {
    return false;
  }

  try {
    // Try to create a date formatter with the timezone
    // This will throw if the timezone is invalid
    Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Validates and normalizes a timezone string
 * @param timezone - Timezone string to validate and normalize
 * @param fallback - Fallback timezone if validation fails
 * @returns Valid timezone string or fallback
 */
export function validateAndNormalizeTimezone(
  timezone: string | null | undefined,
  fallback: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string {
  const normalized = normalizeTimezone(timezone);
  if (normalized && isValidTimezone(normalized)) {
    return normalized;
  }
  return fallback;
}

/**
 * Input shape for converting a tenant-local date/time into a UTC instant.
 *
 * - `date` must be an ISO local date string in the form "YYYY-MM-DD"
 * - `time` (when provided) must be "HH:mm". When omitted/null, midnight is assumed.
 * - `timeZone` must be a valid IANA timezone identifier
 */
export type TenantLocalDateTimeInput = {
  date: string | null | undefined;
  time?: string | null | undefined;
  timeZone: string | null | undefined;
};

/**
 * Converts a tenant-local date/time into a UTC ISO string using the tenant's timezone.
 *
 * This is the canonical helper for normalizing "local" scheduling fields (e.g. dates
 * coming from `scheduledStartDate` + `scheduledStartTime`) into a true UTC instant
 * that is independent of the server's local timezone.
 *
 * Example (tenant in "Australia/Brisbane"):
 *   { date: "2025-11-11", time: null,  timeZone: "Australia/Brisbane" }
 *     -> "2025-11-10T14:00:00.000Z"   // midnight Brisbane
 *
 *   { date: "2025-11-11", time: "09:30", timeZone: "Australia/Brisbane" }
 *     -> "2025-11-10T23:30:00.000Z"   // 9:30am Brisbane
 *
 * Returns null when:
 *   - date is missing/empty
 *   - timezone is invalid/unresolvable
 *   - the input cannot be converted to a valid Date
 */
export function tenantLocalDateTimeToUtcIso(
  input: TenantLocalDateTimeInput
): string | null {
  const { date, time, timeZone } = input;

  if (!date || typeof date !== 'string' || !date.trim()) {
    return null;
  }

  const tz = validateAndNormalizeTimezone(
    timeZone,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const safeDate = date.trim();
  const safeTime = (time && typeof time === 'string' && time.trim()) || '00:00';

  // Basic sanity check to avoid obviously malformed strings; we deliberately keep
  // this lightweight and let Date/Intl runtime handle deeper validation.
  const isoLocal = `${safeDate}T${safeTime}:00`;

  try {
    const utcDate = zonedTimeToUtc(isoLocal, tz);
    if (Number.isNaN(utcDate.getTime())) {
      return null;
    }
    return utcDate.toISOString();
  } catch {
    // If the timezone is somehow unsupported at runtime or the input is malformed,
    // fail safely by returning null so callers can decide how to handle it.
    return null;
  }
}
